import { ReactNode } from "react";
import { Button, Card, Form } from "react-bootstrap";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import SearchField from "@components/Forms/SearchField/SearchField";
import { BatchActionPill, BatchAction } from "./BatchActionPill";
import "./CatalogIndex.css";

export interface AdminListCardSearchProps {
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onClear: () => void;
  label?: string;
}

export interface AdminListCardSecondaryAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface AdminListCardProps {
  title: string;
  onNew?: () => void;
  newButtonLabel?: string;
  secondaryAction?: AdminListCardSecondaryAction;
  searchProps?: AdminListCardSearchProps;
  batchActions?: BatchAction[];
  selectedCount?: number;
  /** Renders without the card border/shadow and title — for a list nested
   *  inside an AdminFormPage tab, where the tab bar already serves as the
   *  header. */
  bare?: boolean;
  /** bare only: hides `children` and shows `emptyMessage` in the header row
   *  instead of the batch pill — mirrors Edit Blueprint's item tabs
   *  (Products/Resources/Governance/Expense). */
  isEmpty?: boolean;
  emptyMessage?: string;
  children: ReactNode;
}

export const AdminListCard = ({
  title,
  onNew,
  newButtonLabel = "+ New",
  secondaryAction,
  searchProps,
  batchActions,
  selectedCount = 0,
  bare = false,
  isEmpty = false,
  emptyMessage,
  children,
}: AdminListCardProps) => {
  const showBatchPill =
    !!batchActions && batchActions.length > 0 && selectedCount > 0;
  const showActionRow = showBatchPill || !!searchProps;

  const actionButtons = (secondaryAction || onNew) && (
    <div className="admin-list-card__title-actions">
      {secondaryAction && (
        <Button
          className="btnSeawhite"
          onClick={secondaryAction.onClick}
          disabled={secondaryAction.disabled}
        >
          {secondaryAction.label}
        </Button>
      )}
      {onNew && (
        <Button className="squareGreenButton" onClick={onNew}>
          {newButtonLabel}
        </Button>
      )}
    </div>
  );

  const actionRow = showActionRow && (
    <div className="admin-list-card__action-row">
      {showBatchPill && (
        <BatchActionPill
          selectedCount={selectedCount}
          actions={batchActions!}
        />
      )}
      {searchProps && (
        <div className="admin-list-card__search-wrapper">
          <FormFieldLabel
            label={searchProps.label ?? "Search"}
            className={undefined as unknown as string}
          />
          <Form
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                searchProps.onSubmit(
                  e as unknown as React.FormEvent<HTMLFormElement>
                );
              }
            }}
            onSubmit={searchProps.onSubmit}
          >
            <SearchField
              placeholder={searchProps.placeholder}
              value={searchProps.value}
              onChange={searchProps.onChange}
              onClick={searchProps.onClear}
              fullWidth
            />
          </Form>
        </div>
      )}
    </div>
  );

  if (bare) {
    const showEmptyMessage = isEmpty && !showBatchPill && !!emptyMessage;
    const hasLeftSlot = showBatchPill || showEmptyMessage;

    return (
      <div className="admin-list-card admin-list-card--bare">
        <div
          className={`admin-list-card__title-row admin-list-card__title-row--bare${
            hasLeftSlot ? " admin-list-card__title-row--split" : ""
          }`}
        >
          {showBatchPill ? (
            <BatchActionPill
              selectedCount={selectedCount}
              actions={batchActions!}
            />
          ) : showEmptyMessage ? (
            <div className="admin-list-card__empty">{emptyMessage}</div>
          ) : null}
          {actionButtons}
        </div>
        {searchProps && (
          <div className="admin-list-card__action-row">
            <div className="admin-list-card__search-wrapper">
              <FormFieldLabel
                label={searchProps.label ?? "Search"}
                className={undefined as unknown as string}
              />
              <Form
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchProps.onSubmit(
                      e as unknown as React.FormEvent<HTMLFormElement>
                    );
                  }
                }}
                onSubmit={searchProps.onSubmit}
              >
                <SearchField
                  placeholder={searchProps.placeholder}
                  value={searchProps.value}
                  onChange={searchProps.onChange}
                  onClick={searchProps.onClear}
                  fullWidth
                />
              </Form>
            </div>
          </div>
        )}
        {!isEmpty && children}
      </div>
    );
  }

  return (
    <div className="admin-list-card legacyPageBleed">
      <Card>
        <Card.Header>
          <div className="admin-list-card__title-row">
            <div className="adminListCardTitle">{title}</div>
            {actionButtons}
          </div>
          <hr />
          {actionRow}
        </Card.Header>
        {children}
      </Card>
    </div>
  );
};
